/**
 * Backend client for the dashboard.
 *
 * Uses the session cookie set by /v1/auth/callback. All requests use
 * credentials: 'include' so cookies travel. In dev, Vite proxies /v1/* to the
 * backend so origins match.
 */

import type { Finding, ProductMetadata } from '@wholisphere/vpat';
import { apiBase } from './api-base.js';

export type Edition = 'INT' | 'WCAG' | '508' | 'EU';

export type Standard =
  | 'WCAG 2.1 Level A'
  | 'WCAG 2.1 Level AA'
  | 'WCAG 2.1 Level AAA'
  | 'WCAG 2.2 Level A'
  | 'WCAG 2.2 Level AA'
  | 'WCAG 2.2 Level AAA'
  | 'EN 301 549'
  | 'Section 508';

export interface ProductSummary {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  defaultEdition: Edition;
  defaultStandards: string[];
  defaultVendorName: string | null;
  defaultContactInfo: string | null;
  defaultProductDescription: string | null;
  homepageUrl: string | null;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
}

export interface CreateProductInput {
  slug: string;
  name: string;
  description?: string;
  defaultEdition?: Edition;
  defaultStandards?: Standard[];
  defaultVendorName?: string;
  defaultContactInfo?: string;
  defaultProductDescription?: string;
  homepageUrl?: string;
}

export interface UpdateProductInput {
  name?: string;
  description?: string | null;
  defaultEdition?: Edition;
  defaultStandards?: Standard[];
  defaultVendorName?: string | null;
  defaultContactInfo?: string | null;
  defaultProductDescription?: string | null;
  homepageUrl?: string | null;
}

export interface DiffDelta {
  criterion: string;
  prev: Finding | null;
  curr: Finding | null;
  rankDelta: number;
  summary: string;
}

/** A proposed source fix for one element (ADR-0006 fix engine). */
export interface FixDescriptorView {
  criterion: string;
  fixType: 'content' | 'design' | 'structural';
  fixScope: 'page' | 'template' | 'site';
  selector: string;
  issue: string;
  change: {
    kind: string;
    name?: string;
    value?: string | null;
    property?: string;
    level?: number;
    note?: string;
  };
  source: 'deterministic' | 'generated' | 'needs-text';
  confidence: number;
  rationale: string;
  snippet?: string;
}

export interface ScanFixesResponse {
  scanId: string;
  fixes: FixDescriptorView[];
  summary: {
    total: number;
    deterministic: number;
    generated: number;
    needsText: number;
    deliverable: number;
  };
  /** Source-only coding-agent task for the deliverable fixes (copy/hand off). */
  agentTask: string;
}

export interface CreateFixPrResponse {
  status: 'opened' | 'skipped';
  pr?: { number: number; url: string };
  changedFiles?: number;
  reason?: string;
}

/** One file's proposed change, as a unified diff — applied to nothing yet. */
export interface FixPreviewFile {
  path: string;
  diff: string;
  added: number;
  removed: number;
}

export interface FixPreviewResponse {
  status: 'previewed' | 'skipped';
  files?: FixPreviewFile[];
  fixes?: number;
  reason?: string;
}

export interface FixPrStatusResponse {
  status: 'none' | 'open' | 'merged' | 'closed';
  pr?: { number: number; url: string };
}

export interface FixOutcome {
  criterion: string;
  status: 'resolved' | 'unresolved' | 'regressed';
}

export interface FixVerificationResponse {
  scanId: string;
  against: string;
  proposed: number;
  resolved: number;
  unresolved: number;
  regressed: number;
  outcomes: FixOutcome[];
}

export interface ApprovalSummary {
  id: string;
  scanId: string;
  vpatHash: string;
  edition: Edition;
  standards: string[];
  approvedByUserId: string;
  approvedAt: string;
  status: 'approved' | 'revoked';
  notes: string | null;
  revokedByUserId: string | null;
  revokedAt: string | null;
  revocationReason: string | null;
}

export interface CreateApprovalInput {
  scanId: string;
  edition: Edition;
  standards: Standard[];
  vendorName: string;
  contactInfo: string;
  productDescription: string;
  notes?: string;
}

export interface AuditIntegrityFinding {
  kind: 'tampered' | 'chain_break' | 'race';
  rowId: string;
  ts: string;
  message: string;
}

export interface AuditIntegrityReport {
  orgId: string;
  chainVersion: number;
  ok: boolean;
  totalRows: number;
  verified: number;
  preChain: number;
  restarts: number;
  findings: AuditIntegrityFinding[];
  latestHash: string;
  truncated: boolean;
}

export interface FindingOverrideRow {
  id: string;
  scanId: string;
  criterion: string;
  originalConformance: string;
  overrideConformance: string;
  reason: string;
  overriddenByUserId: string;
  overriddenAt: string;
  status: 'active' | 'reverted';
  revertedByUserId: string | null;
  revertedAt: string | null;
}

export interface CreateVpatShareLinkInput {
  scanId: string;
  edition: Edition;
  standards: Standard[];
  vendorName: string;
  contactInfo: string;
  productDescription: string;
  notes?: string;
  label?: string;
  /** Days until expiry. 0 = no expiry. Default 30. Capped at 365. */
  expiresInDays?: number;
}

export interface VpatShareLinkCreated {
  id: string;
  scanId: string;
  /** Public URL the customer pastes into procurement email. Shown ONCE. */
  url: string;
  /** Plaintext token (also embedded in url). Shown ONCE. */
  token: string;
  edition: Edition;
  expiresAt: string | null;
  label: string | null;
  createdAt: string;
}

export interface VpatShareLinkSummary {
  id: string;
  scanId: string;
  edition: Edition;
  standards: string[];
  vendorName: string;
  contactInfo: string;
  label: string | null;
  createdByUserId: string;
  createdAt: string;
  expiresAt: string | null;
  revokedAt: string | null;
  revokedByUserId: string | null;
  viewCount: number;
  lastViewedAt: string | null;
}

export interface HealthResponse {
  status: string;
  service: string;
  version: string;
  llm: { providerConfigured: boolean; model: string };
}

export interface ApiKeySummary {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  allowedCidrs: string[];
}

export interface ApiKeyCreated {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  allowedCidrs: string[];
  plaintext: string;
  createdAt: string;
}

export interface AuditEvent {
  id: string;
  ts: string;
  kind: string;
  details: Record<string, unknown>;
  traceId: string | null;
}

export type MemberRole = 'owner' | 'admin' | 'editor' | 'viewer' | 'api';

export interface MemberSummary {
  userId: string;
  email: string;
  displayName: string | null;
  role: MemberRole;
  joinedAt: string;
  self: boolean;
}

export interface InviteSummary {
  id: string;
  email: string;
  role: 'admin' | 'editor' | 'viewer';
  invitedBy: string | null;
  createdAt: string;
  expiresAt: string;
}

export interface InvitePreview {
  email: string;
  role: 'admin' | 'editor' | 'viewer';
  orgName: string;
  expiresAt: string;
}

class HttpError extends Error {
  code: string;
  status: number;
  constructor(message: string, status: number, code: string) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

const fetchJson = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
  const resp = await fetch(`${apiBase()}${path}`, {
    credentials: 'include',
    ...init,
    headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
  });
  const text = await resp.text();
  const json = text ? JSON.parse(text) : null;
  if (!resp.ok) {
    const code =
      (json as { error?: { code?: string } } | null)?.error?.code ?? `http_${resp.status}`;
    const message =
      (json as { error?: { message?: string } } | null)?.error?.message ?? `HTTP ${resp.status}`;
    throw new HttpError(message, resp.status, code);
  }
  return json as T;
};

export interface ProductConformance {
  slug: string;
  name: string;
  scanned: boolean;
  scanId?: string;
  ts?: string;
  score: number | null;
  supports?: number;
  partial?: number;
  doesNot?: number;
}

export interface ConformanceRollup {
  products: ProductConformance[];
  summary: {
    productCount: number;
    returned: number;
    scannedCount: number;
    unscannedCount: number;
    avgScore: number | null;
    worstScore: number | null;
    totalDoesNotSupport: number;
  };
  truncated: boolean;
}

export class DashboardApi {
  health(): Promise<HealthResponse> {
    return fetchJson<HealthResponse>('/v1/health');
  }

  vpat(input: {
    product: ProductMetadata;
    findings: Finding[];
  }): Promise<{
    markdown: string;
    meta: { product: string; version: string; findingCount: number };
  }> {
    return fetchJson('/v1/vpat', { method: 'POST', body: JSON.stringify(input) });
  }

  /** Most recent scan + its findings for a given product/version. */
  latestFindings(
    productName: string,
    productVersion: string,
  ): Promise<{
    scan: {
      id: string;
      productName: string;
      productVersion: string;
      source: string;
      ts: string;
      notes: string | null;
      buildRef: string | null;
    } | null;
    findings: Finding[];
  }> {
    const params = new URLSearchParams({ productName, productVersion });
    return fetchJson(`/v1/findings/latest?${params.toString()}`);
  }

  /** Review queue for a scan — merged findings + human verdicts + progress. */
  reviewQueue(
    orgId: string,
    scanId: string,
    autoConfirmMinConfidence?: number,
  ): Promise<{
    scanId: string;
    progress: { total: number; reviewed: number; remaining: string[]; complete: boolean };
    queue: Array<{
      criterion: string;
      conformance: string;
      remarks: string;
      confidence: number | null;
      reviewState: 'pending' | 'confirmed' | 'skipped' | 'overridden' | 'auto-confirmed';
      override?: { conformance: string; reason: string };
      reviewedAt?: string;
      note?: string | null;
    }>;
  }> {
    const q =
      autoConfirmMinConfidence !== undefined
        ? `?autoConfirmMinConfidence=${autoConfirmMinConfidence}`
        : '';
    return fetchJson(
      `/v1/orgs/${encodeURIComponent(orgId)}/scans/${encodeURIComponent(scanId)}/review${q}`,
    );
  }

  /** Record a human verdict on one criterion. */
  reviewFinding(
    orgId: string,
    scanId: string,
    body: { criterion: string; action: 'confirmed' | 'skipped' | 'clear'; note?: string },
  ): Promise<{
    ok: boolean;
    progress: { total: number; reviewed: number; remaining: string[]; complete: boolean };
  }> {
    return fetchJson(
      `/v1/orgs/${encodeURIComponent(orgId)}/scans/${encodeURIComponent(scanId)}/review`,
      { method: 'POST', body: JSON.stringify(body) },
    );
  }

  /** Freeze an immutable published version (requires review-complete + approval). */
  publishVpat(
    orgId: string,
    scanId: string,
    body: {
      edition: string;
      standards: string[];
      vendorName: string;
      contactInfo: string;
      productDescription: string;
      notes?: string;
      autoConfirmMinConfidence?: number;
    },
  ): Promise<{
    ok: boolean;
    published: { id: string; version: number; contentSha256: string; publishedAt: string };
  }> {
    return fetchJson(
      `/v1/orgs/${encodeURIComponent(orgId)}/scans/${encodeURIComponent(scanId)}/publish`,
      { method: 'POST', body: JSON.stringify(body) },
    );
  }

  /** Published version history for a scan. */
  publishedVersions(
    orgId: string,
    scanId: string,
  ): Promise<{
    versions: Array<{
      id: string;
      version: number;
      edition: string;
      standards: string[];
      contentSha256: string;
      approvalId: string;
      publishedBy: string;
      publishedAt: string;
    }>;
  }> {
    return fetchJson(
      `/v1/orgs/${encodeURIComponent(orgId)}/scans/${encodeURIComponent(scanId)}/published`,
    );
  }

  /** List recent scans across the org. */
  listScans(orgId: string): Promise<{
    scans: Array<{
      id: string;
      productName: string;
      productVersion: string;
      source: string;
      status: 'pending' | 'running' | 'completed' | 'failed' | null;
      startedAt: string;
      completedAt: string | null;
      pageCount: number | null;
      buildRef: string | null;
    }>;
  }> {
    return fetchJson(`/v1/orgs/${encodeURIComponent(orgId)}/scans`);
  }

  /** Org-wide portfolio conformance: every product's latest-scan score + a summary. */
  getConformanceRollup(orgId: string): Promise<ConformanceRollup> {
    return fetchJson(`/v1/orgs/${encodeURIComponent(orgId)}/conformance`);
  }

  /** Create a read-only client portal link for a product. Token shown once. */
  getGitHubIntegration(
    orgId: string,
    slug: string,
  ): Promise<{
    connected: boolean;
    repo?: string;
    minSeverity?: string;
    enabled?: boolean;
    /** Whether the stored token can open PRs (contents + pull-requests write).
     * Omitted when unknown (e.g. a transient GitHub error on the live check). */
    canOpenPrs?: boolean;
  }> {
    return fetchJson(
      `/v1/orgs/${encodeURIComponent(orgId)}/products/${encodeURIComponent(slug)}/github`,
    );
  }

  connectGitHub(
    orgId: string,
    slug: string,
    input: { repoOwner: string; repoName: string; token: string; minSeverity?: string },
  ): Promise<{ ok: boolean; repo: string; minSeverity: string }> {
    return fetchJson(
      `/v1/orgs/${encodeURIComponent(orgId)}/products/${encodeURIComponent(slug)}/github`,
      { method: 'POST', body: JSON.stringify(input) },
    );
  }

  disconnectGitHub(orgId: string, slug: string): Promise<void> {
    return fetchJson(
      `/v1/orgs/${encodeURIComponent(orgId)}/products/${encodeURIComponent(slug)}/github`,
      { method: 'DELETE' },
    );
  }

  createPortalLink(
    orgId: string,
    slug: string,
    input: { label?: string; expiresInDays?: number },
  ): Promise<{
    id: string;
    url: string;
    token: string;
    expiresAt: string | null;
    label: string | null;
  }> {
    return fetchJson(
      `/v1/orgs/${encodeURIComponent(orgId)}/products/${encodeURIComponent(slug)}/portal-links`,
      { method: 'POST', body: JSON.stringify(input) },
    );
  }

  listPortalLinks(
    orgId: string,
    slug: string,
  ): Promise<{
    portalLinks: Array<{
      id: string;
      label: string | null;
      createdAt: string;
      expiresAt: string | null;
      revokedAt: string | null;
      viewCount: number;
      lastViewedAt: string | null;
    }>;
  }> {
    return fetchJson(
      `/v1/orgs/${encodeURIComponent(orgId)}/products/${encodeURIComponent(slug)}/portal-links`,
    );
  }

  revokePortalLink(orgId: string, slug: string, id: string): Promise<void> {
    return fetchJson(
      `/v1/orgs/${encodeURIComponent(orgId)}/products/${encodeURIComponent(slug)}/portal-links/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    );
  }

  /** Engagement wizard: create product + baseline scan + optional schedule. */
  createEngagement(
    orgId: string,
    input: {
      clientName: string;
      urls: string[];
      cadence?: 'none' | 'daily' | 'weekly' | 'monthly';
      failOnRegression?: boolean;
      vendorName?: string;
      contactInfo?: string;
    },
  ): Promise<{
    ok: boolean;
    product: { id: string; slug: string };
    baselineScan?: { scanId: string; jobId: string; status: string } | { error: string };
    schedule?: { id: string; cadence: string; nextRun: string | null } | { error: string };
  }> {
    return fetchJson(`/v1/orgs/${encodeURIComponent(orgId)}/engagements`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  /** Queue a hosted scan — the runner fleet executes it, no CI needed. */
  enqueueHostedScan(
    orgId: string,
    input: {
      productName: string;
      productVersion?: string;
      urls?: string[];
      sitemap?: string;
    },
  ): Promise<{ ok: boolean; scanId: string; jobId: string; status: string }> {
    return fetchJson(`/v1/orgs/${encodeURIComponent(orgId)}/hosted-scans`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  /** Hosted-scan queue history for the org. */
  listHostedScans(orgId: string): Promise<{
    jobs: Array<{
      id: string;
      scanId: string;
      status: 'queued' | 'claimed' | 'done' | 'failed';
      attempts: number;
      targets: { urls?: string[]; sitemap?: string };
      createdAt: string;
      updatedAt: string;
    }>;
  }> {
    return fetchJson(`/v1/orgs/${encodeURIComponent(orgId)}/hosted-scans`);
  }

  /** Scan detail with aggregated per-criterion verdicts. */
  scanDetail(
    orgId: string,
    scanId: string,
  ): Promise<{
    scan: {
      id: string;
      productName: string;
      productVersion: string;
      source: string;
      status: 'pending' | 'running' | 'completed' | 'failed' | null;
      startedAt: string;
      completedAt: string | null;
      pageCount: number | null;
      buildRef: string | null;
    };
    aggregated: Array<{
      criterion: string;
      conformance:
        | 'Supports'
        | 'Partially Supports'
        | 'Does Not Support'
        | 'Not Applicable'
        | 'Not Evaluated';
      remarks: string;
      evidence: string[];
      confidence: number;
      pageCount: number;
      contributingCapabilities: string[];
    }>;
    rawFindingCount: number;
  }> {
    return fetchJson(`/v1/orgs/${encodeURIComponent(orgId)}/scans/${encodeURIComponent(scanId)}`);
  }

  /** Diff two scans. */
  scanDiff(
    orgId: string,
    currScanId: string,
    againstScanId: string,
  ): Promise<{
    prev: {
      id: string;
      productName: string;
      productVersion: string;
      completedAt: string | null;
      buildRef: string | null;
    };
    curr: {
      id: string;
      productName: string;
      productVersion: string;
      completedAt: string | null;
      buildRef: string | null;
    };
    counts: {
      regressed: number;
      improved: number;
      unchanged: number;
      introduced: number;
      removed: number;
      total: number;
    };
    hasRegressions: boolean;
    regressed: Array<DiffDelta>;
    improved: Array<DiffDelta>;
    introduced: Array<DiffDelta>;
    removed: Array<DiffDelta>;
  }> {
    return fetchJson(
      `/v1/orgs/${encodeURIComponent(orgId)}/scans/${encodeURIComponent(currScanId)}/diff?against=${encodeURIComponent(againstScanId)}`,
    );
  }

  /** Proposed source fixes generated for a completed scan (ADR-0006). */
  scanFixes(orgId: string, scanId: string): Promise<ScanFixesResponse> {
    return fetchJson(
      `/v1/orgs/${encodeURIComponent(orgId)}/scans/${encodeURIComponent(scanId)}/fixes`,
    );
  }

  /** Open a PR applying the deliverable fixes to the connected repo (Mode A). */
  createFixPr(orgId: string, scanId: string): Promise<CreateFixPrResponse> {
    return fetchJson(
      `/v1/orgs/${encodeURIComponent(orgId)}/scans/${encodeURIComponent(scanId)}/fixes/pr`,
      { method: 'POST' },
    );
  }

  /** Dry-run: the exact per-file source diff each fix would make — opens no PR. */
  previewFixPr(orgId: string, scanId: string): Promise<FixPreviewResponse> {
    return fetchJson(
      `/v1/orgs/${encodeURIComponent(orgId)}/scans/${encodeURIComponent(scanId)}/fixes/preview`,
      { method: 'POST' },
    );
  }

  /** Current state of the fix PR for a scan (none/open/merged/closed). */
  fixPrStatus(orgId: string, scanId: string): Promise<FixPrStatusResponse> {
    return fetchJson(
      `/v1/orgs/${encodeURIComponent(orgId)}/scans/${encodeURIComponent(scanId)}/fixes/pr`,
    );
  }

  /** Verify which of a scan's proposed fixes a later re-scan actually resolved. */
  verifyFixes(
    orgId: string,
    scanId: string,
    againstScanId: string,
  ): Promise<FixVerificationResponse> {
    return fetchJson(
      `/v1/orgs/${encodeURIComponent(orgId)}/scans/${encodeURIComponent(scanId)}/fixes/verify?against=${encodeURIComponent(againstScanId)}`,
    );
  }

  /** Products CRUD. */
  listProducts(orgId: string, includeArchived = false): Promise<{ products: ProductSummary[] }> {
    const qs = includeArchived ? '?archived=true' : '';
    return fetchJson(`/v1/orgs/${encodeURIComponent(orgId)}/products${qs}`);
  }
  getProduct(orgId: string, slug: string): Promise<ProductSummary> {
    return fetchJson(`/v1/orgs/${encodeURIComponent(orgId)}/products/${encodeURIComponent(slug)}`);
  }
  createProduct(orgId: string, body: CreateProductInput): Promise<ProductSummary> {
    return fetchJson(`/v1/orgs/${encodeURIComponent(orgId)}/products`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }
  updateProduct(orgId: string, slug: string, body: UpdateProductInput): Promise<ProductSummary> {
    return fetchJson(`/v1/orgs/${encodeURIComponent(orgId)}/products/${encodeURIComponent(slug)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }
  archiveProduct(
    orgId: string,
    slug: string,
  ): Promise<{ id: string; slug: string; status: string }> {
    return fetchJson(`/v1/orgs/${encodeURIComponent(orgId)}/products/${encodeURIComponent(slug)}`, {
      method: 'DELETE',
    });
  }
  restoreProduct(
    orgId: string,
    slug: string,
  ): Promise<{ id: string; slug: string; status: string }> {
    return fetchJson(
      `/v1/orgs/${encodeURIComponent(orgId)}/products/${encodeURIComponent(slug)}/restore`,
      { method: 'POST' },
    );
  }

  /** VPAT approvals. */
  listApprovals(orgId: string): Promise<{ approvals: ApprovalSummary[] }> {
    return fetchJson(`/v1/orgs/${encodeURIComponent(orgId)}/vpat-approvals`);
  }
  createApproval(orgId: string, body: CreateApprovalInput): Promise<ApprovalSummary> {
    return fetchJson(`/v1/orgs/${encodeURIComponent(orgId)}/vpat-approvals`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }
  revokeApproval(
    orgId: string,
    approvalId: string,
    reason: string,
  ): Promise<{ id: string; status: string }> {
    return fetchJson(
      `/v1/orgs/${encodeURIComponent(orgId)}/vpat-approvals/${encodeURIComponent(approvalId)}`,
      { method: 'DELETE', body: JSON.stringify({ reason }) },
    );
  }
  latestApprovalForScan(
    orgId: string,
    scanId: string,
  ): Promise<{ approval: ApprovalSummary | null }> {
    return fetchJson(
      `/v1/orgs/${encodeURIComponent(orgId)}/scans/${encodeURIComponent(scanId)}/vpat-approval`,
    );
  }

  /** Verify the org's audit hash chain. Read-only — safe for any member. */
  verifyAuditIntegrity(orgId: string): Promise<AuditIntegrityReport> {
    return fetchJson(`/v1/orgs/${encodeURIComponent(orgId)}/audit/integrity`, {
      method: 'POST',
    });
  }

  /** Override a per-criterion verdict on a scan (admin+). */
  overrideFinding(
    orgId: string,
    scanId: string,
    body: {
      criterion: string;
      conformance:
        | 'Supports'
        | 'Partially Supports'
        | 'Does Not Support'
        | 'Not Applicable'
        | 'Not Evaluated';
      reason: string;
      originalConformance:
        | 'Supports'
        | 'Partially Supports'
        | 'Does Not Support'
        | 'Not Applicable'
        | 'Not Evaluated';
    },
  ): Promise<FindingOverrideRow> {
    return fetchJson(
      `/v1/orgs/${encodeURIComponent(orgId)}/scans/${encodeURIComponent(scanId)}/findings/override`,
      { method: 'POST', body: JSON.stringify(body) },
    );
  }
  revertFindingOverride(
    orgId: string,
    scanId: string,
    criterion: string,
  ): Promise<{ scanId: string; criterion: string; status: 'reverted' }> {
    return fetchJson(
      `/v1/orgs/${encodeURIComponent(orgId)}/scans/${encodeURIComponent(scanId)}/findings/override`,
      { method: 'DELETE', body: JSON.stringify({ criterion }) },
    );
  }

  /** Generate a public share link for a VPAT (admin+). The plaintext token
   *  is in the response.url and response.token — shown ONCE; surface it to
   *  the user immediately. */
  createVpatShareLink(
    orgId: string,
    body: CreateVpatShareLinkInput,
  ): Promise<VpatShareLinkCreated> {
    return fetchJson(`/v1/orgs/${encodeURIComponent(orgId)}/vpat-share-links`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }
  listVpatShareLinks(orgId: string): Promise<{ shareLinks: VpatShareLinkSummary[] }> {
    return fetchJson(`/v1/orgs/${encodeURIComponent(orgId)}/vpat-share-links`);
  }
  revokeVpatShareLink(orgId: string, id: string): Promise<{ id: string; status: string }> {
    return fetchJson(
      `/v1/orgs/${encodeURIComponent(orgId)}/vpat-share-links/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    );
  }

  invalidateCache(input: {
    urls: string[];
    reason?: string;
  }): Promise<{ invalidated: number; traceId: string }> {
    return fetchJson('/v1/cache/invalidate', { method: 'POST', body: JSON.stringify(input) });
  }

  listApiKeys(orgId: string): Promise<{ keys: ApiKeySummary[] }> {
    return fetchJson(`/v1/orgs/${encodeURIComponent(orgId)}/keys`);
  }

  createApiKey(
    orgId: string,
    body: { name: string; scopes?: string[]; allowedCidrs?: string[] },
  ): Promise<ApiKeyCreated> {
    return fetchJson(`/v1/orgs/${encodeURIComponent(orgId)}/keys`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  revokeApiKey(orgId: string, keyId: string): Promise<{ ok: boolean }> {
    return fetchJson(
      `/v1/orgs/${encodeURIComponent(orgId)}/keys/${encodeURIComponent(keyId)}/revoke`,
      {
        method: 'POST',
      },
    );
  }

  listAudit(
    orgId: string,
    filter: { kind?: string; actor?: string; since?: string; until?: string; limit?: number } = {},
  ): Promise<{ events: AuditEvent[] }> {
    const qs = new URLSearchParams();
    qs.set('limit', String(filter.limit ?? 200));
    if (filter.kind) qs.set('kind', filter.kind);
    if (filter.actor) qs.set('actor', filter.actor);
    if (filter.since) qs.set('since', filter.since);
    if (filter.until) qs.set('until', filter.until);
    return fetchJson(`/v1/orgs/${encodeURIComponent(orgId)}/audit?${qs.toString()}`);
  }

  auditCsvUrl(
    orgId: string,
    filter: { kind?: string; actor?: string; since?: string; until?: string; limit?: number } = {},
  ): string {
    const qs = new URLSearchParams();
    qs.set('limit', String(filter.limit ?? 1000));
    if (filter.kind) qs.set('kind', filter.kind);
    if (filter.actor) qs.set('actor', filter.actor);
    if (filter.since) qs.set('since', filter.since);
    if (filter.until) qs.set('until', filter.until);
    return `${apiBase()}/v1/orgs/${encodeURIComponent(orgId)}/audit.csv?${qs.toString()}`;
  }

  startCheckout(
    orgId: string,
    plan: 'pro' | 'platform',
  ): Promise<{ url: string; sessionId: string }> {
    return fetchJson('/v1/billing/checkout', {
      method: 'POST',
      body: JSON.stringify({ orgId, plan }),
    });
  }

  startTrial(
    orgId: string,
    plan: 'pro' | 'platform' = 'pro',
  ): Promise<{ url: string; sessionId: string }> {
    return fetchJson('/v1/billing/start-trial', {
      method: 'POST',
      body: JSON.stringify({ orgId, plan }),
    });
  }

  openPortal(orgId: string): Promise<{ url: string }> {
    return fetchJson('/v1/billing/portal', {
      method: 'POST',
      body: JSON.stringify({ orgId }),
    });
  }

  listMembers(orgId: string): Promise<{ members: MemberSummary[] }> {
    return fetchJson(`/v1/orgs/${encodeURIComponent(orgId)}/members`);
  }

  updateMemberRole(orgId: string, userId: string, role: MemberRole): Promise<{ ok: boolean }> {
    return fetchJson(
      `/v1/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(userId)}`,
      { method: 'PATCH', body: JSON.stringify({ role }) },
    );
  }

  removeMember(orgId: string, userId: string): Promise<{ ok: boolean }> {
    return fetchJson(
      `/v1/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(userId)}`,
      { method: 'DELETE' },
    );
  }

  listInvites(orgId: string): Promise<{ invites: InviteSummary[] }> {
    return fetchJson(`/v1/orgs/${encodeURIComponent(orgId)}/invites`);
  }

  createInvite(
    orgId: string,
    body: { email: string; role: 'admin' | 'editor' | 'viewer' },
  ): Promise<InviteSummary> {
    return fetchJson(`/v1/orgs/${encodeURIComponent(orgId)}/invites`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  revokeInvite(orgId: string, inviteId: string): Promise<{ ok: boolean }> {
    return fetchJson(
      `/v1/orgs/${encodeURIComponent(orgId)}/invites/${encodeURIComponent(inviteId)}`,
      { method: 'DELETE' },
    );
  }

  previewInvite(token: string): Promise<InvitePreview> {
    return fetchJson(`/v1/invites/${encodeURIComponent(token)}`);
  }

  acceptInvite(token: string): Promise<{ ok: boolean; orgId: string; role: MemberRole }> {
    return fetchJson(`/v1/invites/${encodeURIComponent(token)}/accept`, { method: 'POST' });
  }

  getUsage(orgId: string, month?: string): Promise<UsageSummary> {
    const q = month ? `?month=${encodeURIComponent(month)}` : '';
    return fetchJson(`/v1/orgs/${encodeURIComponent(orgId)}/usage${q}`);
  }

  getCost(orgId: string, month?: string): Promise<CostSummary> {
    const q = month ? `?month=${encodeURIComponent(month)}` : '';
    return fetchJson(`/v1/orgs/${encodeURIComponent(orgId)}/cost${q}`);
  }

  getSso(orgId: string): Promise<SsoConfig> {
    return fetchJson(`/v1/orgs/${encodeURIComponent(orgId)}/sso`);
  }

  updateSso(
    orgId: string,
    body: {
      allowedDomain: string;
      autoAssignRole: 'admin' | 'editor' | 'viewer';
      enforce: boolean;
    },
  ): Promise<{ ok: boolean }> {
    return fetchJson(`/v1/orgs/${encodeURIComponent(orgId)}/sso`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  disableSso(orgId: string): Promise<{ ok: boolean }> {
    return fetchJson(`/v1/orgs/${encodeURIComponent(orgId)}/sso`, { method: 'DELETE' });
  }

  updateOrgRegion(orgId: string, region: 'us' | 'eu' | 'apac'): Promise<{ ok: boolean }> {
    return fetchJson(`/v1/orgs/${encodeURIComponent(orgId)}/region`, {
      method: 'PATCH',
      body: JSON.stringify({ region }),
    });
  }

  getDigestPref(orgId: string): Promise<{ enabled: boolean; lastSentAt: string | null }> {
    return fetchJson(`/v1/orgs/${encodeURIComponent(orgId)}/digest`);
  }

  setDigestPref(orgId: string, enabled: boolean): Promise<{ ok: boolean; enabled: boolean }> {
    return fetchJson(`/v1/orgs/${encodeURIComponent(orgId)}/digest`, {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    });
  }

  getNotificationSettings(
    orgId: string,
  ): Promise<{ regressionEmail: boolean; completionEmail: boolean }> {
    return fetchJson(`/v1/orgs/${encodeURIComponent(orgId)}/notifications`);
  }

  setNotificationSettings(
    orgId: string,
    settings: { regressionEmail: boolean; completionEmail: boolean },
  ): Promise<{ ok: boolean; regressionEmail: boolean; completionEmail: boolean }> {
    return fetchJson(`/v1/orgs/${encodeURIComponent(orgId)}/notifications`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  listWebhookEventKinds(): Promise<{
    kinds: Array<{ kind: string; description: string; examplePayload: Record<string, unknown> }>;
  }> {
    return fetchJson('/v1/webhook-event-kinds');
  }

  listWebhooks(orgId: string): Promise<{ webhooks: WebhookSubscription[] }> {
    return fetchJson(`/v1/orgs/${encodeURIComponent(orgId)}/webhooks`);
  }

  createWebhook(orgId: string, body: { url: string; events: string[] }): Promise<WebhookCreated> {
    return fetchJson(`/v1/orgs/${encodeURIComponent(orgId)}/webhooks`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  deleteWebhook(orgId: string, id: string): Promise<{ ok: boolean }> {
    return fetchJson(`/v1/orgs/${encodeURIComponent(orgId)}/webhooks/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }
}

export interface WebhookSubscription {
  id: string;
  url: string;
  events: string[];
  secretPrefix: string;
  active: boolean;
  createdAt: string;
  lastDeliveryAt: string | null;
  lastDeliveryStatus: string | null;
  failureCount: number;
}

export interface WebhookCreated {
  id: string;
  url: string;
  events: string[];
  secret: string;
  secretPrefix: string;
  createdAt: string;
}

export type SsoConfig =
  | { enabled: false }
  | {
      enabled: true;
      provider: 'google';
      allowedDomain: string;
      autoAssignRole: 'admin' | 'editor' | 'viewer';
      enforce: boolean;
    };

export interface UsageSummary {
  month: string;
  plan: 'free' | 'pro' | 'platform' | 'enterprise';
  limit: number | null;
  used: number;
  remaining: number | null;
  byCapability: Record<string, number>;
}

export interface CostSummary {
  month: string;
  totalCostUsd: number;
  cacheSavedUsd: number;
  cacheHitRatePct: number | null;
  byCapability: Array<{
    capability: string;
    invocations: number;
    cloudCalls: number;
    cacheHits: number;
    tokensIn: number;
    tokensOut: number;
    costUsd: number;
    cacheSavedUsd: number;
  }>;
}

export { HttpError };
